import type { ZodError, ZodIssue } from 'zod';
import type {
  ValidationError,
  StandardError,
  ErrorCode,
  ErrorBuilderOptions,
} from './models/error';

// Default error configurations
const ERROR_CONFIGS: Record<
  ErrorCode,
  { status: number; retryable: boolean; title?: string }
> = {
  // Authentication
  'auth.unauthorized': {
    status: 401,
    retryable: false,
    title: 'Authentication required',
  },
  'auth.invalidCredentials': {
    status: 401,
    retryable: false,
    title: 'Invalid credentials',
  },
  'auth.tokenExpired': {
    status: 401,
    retryable: false,
    title: 'Token expired',
  },
  'auth.tokenInvalid': {
    status: 401,
    retryable: false,
    title: 'Invalid token',
  },
  'auth.emailNotVerified': {
    status: 401,
    retryable: false,
    title: 'Email not verified',
  },

  // Permission
  'permission.denied': {
    status: 403,
    retryable: false,
    title: 'Permission denied',
  },
  'permission.insufficientRole': {
    status: 403,
    retryable: false,
    title: 'Insufficient permissions',
  },
  'permission.resourceAccess': {
    status: 403,
    retryable: false,
    title: 'Resource access denied',
  },

  // Validation
  'validation.invalid': {
    status: 400,
    retryable: false,
    title: 'Validation failed',
  },
  'validation.required': {
    status: 400,
    retryable: false,
    title: 'Missing required fields',
  },
  'validation.format': {
    status: 400,
    retryable: false,
    title: 'Invalid format',
  },
  'validation.constraint': {
    status: 400,
    retryable: false,
    title: 'Constraint violation',
  },

  // Resource
  'resource.notFound': {
    status: 404,
    retryable: false,
    title: 'Resource not found',
  },
  'resource.conflict': {
    status: 409,
    retryable: false,
    title: 'Resource conflict',
  },
  'resource.duplicate': {
    status: 409,
    retryable: false,
    title: 'Duplicate resource',
  },
  'resource.relationshipViolation': {
    status: 409,
    retryable: false,
    title: 'Relationship constraint violated',
  },

  // System
  'system.internal': {
    status: 500,
    retryable: true,
    title: 'Internal server error',
  },
  'system.database': { status: 500, retryable: true, title: 'Database error' },
  'system.external': {
    status: 502,
    retryable: true,
    title: 'External service error',
  },
  'system.timeout': { status: 504, retryable: true, title: 'Request timeout' },

  // Payment / Subscription
  'payment.required': {
    status: 403,
    retryable: false,
    title: 'Subscription required',
  },

  // Device / hardware — a well-formed request blocked by device state the
  // operator can fix (e.g. Solo reader offline). 422, not 502: this is an
  // expected, user-actionable, retryable condition — never a server outage.
  'device.unavailable': {
    status: 422,
    retryable: true,
    title: 'Device unavailable',
  },

  // Rate limiting
  'rate_limit.exceeded': {
    status: 429,
    retryable: true,
    title: 'Rate limit exceeded',
  },
  'rate_limit.quota': { status: 429, retryable: true, title: 'Quota exceeded' },
};

export function createStandardError(
  code: ErrorCode | string,
  options: ErrorBuilderOptions = {},
): StandardError {
  // Resolve unique error codes to standard ErrorCode types for config lookup
  const resolvedCode = resolveErrorCode(code);
  const config = ERROR_CONFIGS[resolvedCode];

  return {
    status: config.status,
    code: typeof code === 'string' ? code : resolvedCode, // Use the original code (unique or standard)
    retryable: options.retryable ?? config.retryable,
    title: options.title ?? config.title,
    detail: options.detail,
    errors: options.errors,
    timestamp: new Date().toISOString(),
    traceId: options.traceId,
    correlationId: options.correlationId,
  };
}

export const isStandardError = (error: unknown): error is StandardError =>
  !!error &&
  typeof error === 'object' &&
  'status' in error &&
  'code' in error &&
  'retryable' in error &&
  'timestamp' in error;

// Convert Zod errors to our ValidationError format
export function zodErrorToValidationErrors(
  zodError: ZodError,
): ValidationError[] {
  return zodError.errors.map((issue: ZodIssue) => ({
    code: getValidationErrorCode(issue),
    message: issue.message,
    pointer: `/${issue.path.join('/')}`,
  }));
}

// Mapping from Zod error codes to our specific error codes
const ZOD_ERROR_CODE_MAPPING: Record<string, string> = {
  // Size constraint errors
  too_big: 'e00313', // Value too large (generic maximum constraint)
  too_small: 'e00312', // Value too small (generic minimum constraint)

  // Type and format errors
  invalid_type: 'e00318', // Missing required field
  invalid_string: 'e00319', // Invalid string format
  invalid_email: 'e00315', // Invalid email format
  invalid_url: 'e00316', // Invalid URL format
  invalid_enum_value: 'e00317', // Invalid enum value
  invalid_date: 'e00314', // Invalid date format

  // Custom validation errors
  custom: 'e00041', // Generic validation error
  invalid_arguments: 'e00041', // Generic validation error
  unrecognized_keys: 'e00041', // Generic validation error
};

// Shared function to map Zod validation issues to our error codes
export function mapZodIssueToErrorCode(issue: {
  code: string;
  path?: (string | number)[];
}): string {
  // Check for specific field-based mappings first
  if (
    issue.path &&
    issue.path.includes('perPage') &&
    issue.code === 'too_big'
  ) {
    return 'e00310'; // perPage value too large (exceeds maximum)
  }
  if (issue.path && issue.path.includes('page') && issue.code === 'too_small') {
    return 'e00311'; // page value too small (below minimum)
  }

  // Use generic Zod error code mapping
  const mappedCode = ZOD_ERROR_CODE_MAPPING[issue.code];
  if (mappedCode) {
    return mappedCode;
  }

  // Fallback to generic validation error
  return 'e00041';
}

function getValidationErrorCode(issue: ZodIssue): string {
  // Use the shared mapping function
  const mappedCode = mapZodIssueToErrorCode(issue);

  // Handle special case for invalid_type with undefined (missing field)
  if (issue.code === 'invalid_type' && issue.received === 'undefined') {
    return 'e00318'; // Missing required field
  }

  return mappedCode;
}

// Helper functions for common error patterns
export const createAuthError = (
  detail?: string,
  options?: Omit<ErrorBuilderOptions, 'detail'>,
) => createStandardError('auth.unauthorized', { detail, ...options });

export const createPermissionError = (
  detail?: string,
  options?: Omit<ErrorBuilderOptions, 'detail'>,
) => createStandardError('permission.denied', { detail, ...options });

export const createValidationError = (
  zodError?: ZodError,
  detail?: string,
  options?: Omit<ErrorBuilderOptions, 'detail' | 'errors'>,
) => {
  const errors = zodError ? zodErrorToValidationErrors(zodError) : undefined;
  return createStandardError('validation.invalid', {
    detail,
    errors,
    ...options,
  });
};

export const createNotFoundError = (
  resource: string,
  options?: Omit<ErrorBuilderOptions, 'detail'>,
) =>
  createStandardError('resource.notFound', {
    detail: `${resource} not found`,
    ...options,
  });

export const createConflictError = (
  detail?: string,
  options?: Omit<ErrorBuilderOptions, 'detail'>,
) => createStandardError('resource.conflict', { detail, ...options });

export const createSystemError = (
  detail?: string,
  options?: Omit<ErrorBuilderOptions, 'detail'>,
) => createStandardError('system.internal', { detail, ...options });

// Generate a simple trace ID (can be enhanced with proper distributed tracing later)
export function generateTraceId(): string {
  return (
    Math.random().toString(36).substring(2, 15) +
    Math.random().toString(36).substring(2, 15)
  );
}

// Generate correlation ID using timestamp and random
export function generateCorrelationId(): string {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 8).toUpperCase();
  return `${timestamp}${random}`;
}

// Unique error code mapping - maps e00001, e00002, etc. to existing ErrorCode types
export const UNIQUE_ERROR_CODE_MAPPING: Record<string, ErrorCode> = {
  // Authentication Errors
  e00001: 'auth.unauthorized',
  e00002: 'auth.unauthorized', // Unauthorized Telegram token
  e00003: 'auth.unauthorized', // Invalid delete token
  e00004: 'auth.unauthorized', // Invalid change email token
  e00005: 'auth.unauthorized', // Customer invitation required
  e00006: 'auth.unauthorized', // Customer role not found
  e00007: 'auth.unauthorized', // Customer role shop mismatch
  e00008: 'auth.unauthorized', // Invalid verification code
  e00009: 'auth.unauthorized', // Permission denied (auth context)
  e00010: 'auth.tokenInvalid', // Invalid token
  e00011: 'auth.invalidCredentials', // Invalid signature
  e00012: 'auth.unauthorized', // Portal is for business employees only (customer tried to register)

  // Permission Errors
  e00021: 'permission.denied',
  e00022: 'permission.denied', // Permission denied specific

  // Validation Errors
  e00041: 'validation.invalid', // Generic validation error
  e00042: 'validation.invalid', // Failed to list organisations
  e00043: 'validation.invalid', // Failed to get organisation
  e00044: 'validation.invalid', // Failed to create organisation
  e00045: 'validation.invalid', // Failed to update organisation
  e00046: 'validation.invalid', // Failed to delete organisation
  e00047: 'validation.invalid', // Failed to list services
  e00048: 'validation.invalid', // Failed to get service
  e00049: 'validation.invalid', // Failed to create service
  e00050: 'validation.invalid', // Failed to update service
  e00051: 'validation.invalid', // Failed to delete service
  e00052: 'validation.invalid', // Failed to list shops
  e00053: 'validation.invalid', // Failed to create shop
  e00054: 'validation.invalid', // Failed to get shop
  e00055: 'validation.invalid', // Failed to update shop
  e00056: 'validation.invalid', // Failed to delete shop
  e00057: 'validation.constraint', // Upload size exceeded
  e00058: 'validation.invalid', // Failed to list business hours
  e00059: 'validation.invalid', // Failed to create business hours
  e00060: 'validation.invalid', // Failed to update business hours
  e00061: 'validation.invalid', // Failed to get business hours
  e00062: 'validation.invalid', // Failed to delete business hours
  e00063: 'validation.invalid', // Invalid query condition
  e00064: 'validation.invalid', // Invalid expand parameter
  e00065: 'validation.invalid', // Invalid status parameter
  e00066: 'validation.invalid', // Failed to list bookings
  e00067: 'validation.invalid', // Failed to get booking
  e00068: 'validation.required', // Token required
  e00069: 'validation.constraint', // Booking already cancelled
  e00070: 'validation.required', // Missing Stripe signature
  e00071: 'validation.invalid', // Invalid Stripe payload
  e00072: 'validation.invalid', // Failed to get shops
  e00073: 'validation.invalid', // Failed to list staff
  e00074: 'validation.required', // Missing required parameter
  e00075: 'validation.required', // Missing date parameters
  e00076: 'validation.constraint', // Shop country not configured
  e00077: 'validation.invalid', // Invalid parameters
  e00078: 'validation.invalid', // Booking creation failed
  e00079: 'validation.invalid', // Booking cancellation failed
  e00080: 'validation.invalid', // Image delete failed

  // Resource Not Found Errors
  e00081: 'resource.notFound', // Organization not found
  e00082: 'resource.notFound', // Service not found
  e00083: 'resource.notFound', // Shop not found
  e00084: 'resource.notFound', // Business hours not found
  e00085: 'resource.notFound', // Holiday allowance not found
  e00086: 'resource.notFound', // Staff availability not found
  e00087: 'resource.notFound', // User role not found
  e00088: 'resource.notFound', // Booking not found
  e00089: 'resource.notFound', // User not found
  e00090: 'resource.notFound', // Shop not found (invalid ID)
  e00091: 'resource.notFound', // Organisation not found

  // Conflict Errors
  e00121: 'resource.conflict', // Staff limit reached
  e00122: 'resource.conflict', // Email must be different
  e00123: 'resource.conflict', // Email already in use
  e00124: 'resource.conflict', // Holiday allowance conflict
  e00125: 'resource.conflict', // Booking time conflict (staff already booked)
  e00403: 'resource.conflict', // User already exists (duplicate registration)

  // System/Internal Errors
  e00141: 'system.internal', // Failed to list shops
  e00142: 'system.internal', // Failed to create shop
  e00143: 'system.internal', // Failed to get shop
  e00144: 'system.internal', // Failed to delete shop
  e00145: 'system.internal', // Upload failed
  e00146: 'system.internal', // Failed to list bookings
  e00147: 'system.internal', // Failed to get booking
  e00148: 'system.internal', // Failed to get time slots
  e00149: 'system.internal', // Database connection required
  e00150: 'system.internal', // Failed to fetch updated user

  // Additional missed error codes
  e00151: 'validation.required', // URL name required
  e00152: 'validation.invalid', // Failed to generate upload URL
  e00153: 'resource.notFound', // No profile image
  e00154: 'validation.invalid', // Invalid shop organisation
  e00155: 'system.internal', // Webhook secret not configured
  e00156: 'system.internal', // Auth0 registration error
  e00157: 'system.internal', // General user creation error
  e00158: 'system.internal', // Failed to check email
  e00159: 'system.internal', // Registration failed
  e00160: 'system.internal', // Send OTP error
  e00161: 'system.internal', // Failed to send delete account email
  e00162: 'system.internal', // Failed to delete account
  e00163: 'system.internal', // Failed to process change email request
  e00164: 'system.internal', // Failed to send change email confirmation
  e00165: 'system.internal', // Email change - user not linked to auth
  e00166: 'system.internal', // Email change - auth system update failed
  e00167: 'system.internal', // Email change - database update failed
  e00168: 'system.internal', // Failed to change email
  e00169: 'system.internal', // User role operation error
  e00170: 'system.internal', // Reports operation error
  e00171: 'system.internal', // Holiday allowance list error
  e00172: 'system.internal', // Failed to get holiday allowances
  e00173: 'system.internal', // Holiday allowance get error
  e00174: 'system.internal', // Failed to get holiday allowance
  e00175: 'system.internal', // Holiday allowance update error
  e00176: 'system.internal', // Failed to update holiday allowance
  e00177: 'system.internal', // Failed to delete holiday allowance
  e00178: 'system.internal', // Error handler - known error
  e00179: 'system.internal', // Error handler - unexpected error

  // Subscription-specific errors
  e00180: 'validation.invalid', // Invalid subscription plan
  e00181: 'resource.conflict', // Staff limit exceeded for selected plan
  e00182: 'system.internal', // Failed to create checkout session
  e00183: 'system.internal', // Failed to get subscription status
  e00184: 'system.internal', // Failed to check payment requirement
  e00185: 'system.internal', // Failed to get debug info
  e00186: 'resource.notFound', // No Stripe customer found for cancellation
  e00187: 'resource.notFound', // No active subscription found for cancellation
  e00188: 'system.internal', // Failed to cancel subscription
  e00189: 'system.internal', // Webhook handler failed
  e00190: 'resource.notFound', // No Stripe customer found for billing portal
  e00191: 'system.internal', // Failed to create customer portal session
  e00720: 'payment.required', // Subscription expired — read-only mode

  // Token-specific errors
  e00192: 'system.internal', // Failed to get token status
  e00193: 'resource.notFound', // Token data not found
  e00194: 'validation.constraint', // Auto top-up limit validation error
  e00195: 'validation.required', // No valid settings provided
  e00196: 'system.internal', // Failed to update token settings
  e00197: 'validation.required', // Valid token amount required
  e00198: 'validation.constraint', // Maximum tokens per manual top-up exceeded
  e00199: 'system.internal', // Failed to add tokens
  e00200: 'system.internal', // Failed to process manual top-up
  e00201: 'validation.required', // Valid tokenAmount required
  e00202: 'validation.constraint', // Maximum tokens per purchase exceeded
  e00203: 'system.internal', // Frontend URL not configured
  e00204: 'system.internal', // Failed to create checkout session
  e00205: 'system.internal', // Failed to setup auto top-up
  e00206: 'validation.required', // Valid paymentMethodId required
  e00207: 'system.internal', // Failed to update payment method
  e00208: 'validation.constraint', // Email already in use
  e00209: 'system.database', // D1 database timeout
  e00210: 'validation.constraint', // Booking too early (advance booking hours)
  e00211: 'validation.constraint', // Booking too far in advance (max advance booking days)

  // Password authentication errors (e00220 series)
  e00220: 'auth.invalidCredentials', // Invalid email or password
  e00221: 'auth.unauthorized', // Password authentication not enabled
  e00222: 'auth.invalidCredentials', // Password login failed
  e00223: 'system.internal', // Password login system error
  e00224: 'resource.conflict', // Password authentication already enabled
  e00225: 'validation.invalid', // Passwords do not match
  e00226: 'system.internal', // User not linked to Auth0
  e00227: 'system.internal', // Failed to enable password authentication
  e00228: 'system.internal', // Failed to enable password authentication (generic)
  e00229: 'validation.invalid', // Password authentication not enabled (for change)
  e00230: 'validation.invalid', // New passwords do not match
  e00231: 'validation.invalid', // Current password is incorrect (change password)
  e00232: 'system.internal', // User not linked to Auth0 (change password)
  e00233: 'system.internal', // Failed to change password
  e00234: 'system.internal', // Failed to change password (generic)
  e00235: 'validation.invalid', // Password authentication not enabled (for disable)
  e00236: 'resource.conflict', // Cannot disable password while passkeys enabled
  e00237: 'system.internal', // User not linked to Auth0 (disable)
  e00238: 'system.internal', // Failed to disable password authentication
  e00239: 'validation.invalid', // Current password is incorrect (disable password)
  e00240: 'system.internal', // Failed to get auth methods

  // BetterAuth specific password errors (e00247 series)
  e00247: 'validation.invalid', // Current password is incorrect (BetterAuth)
  e00248: 'system.external', // BetterAuth API error
  e00249: 'system.internal', // Failed to parse BetterAuth API error

  // e00241/e00242 retired (ADR 0008): staff↔customer dual-membership is now allowed.
  e00243: 'system.database', // D1 database timeout - server error, please try again

  // Customer search errors (e00244 series)
  e00244: 'validation.invalid', // Invalid entity type. Must be "organisations" or "shops"
  e00245: 'validation.invalid', // Search query (q) is only supported for shops
  e00246: 'system.internal', // Failed to get customers

  // Multi-segment booking errors (e00250 series)
  e00250: 'validation.constraint', // Multi-segment bookings must have at least 2 segments
  e00251: 'validation.constraint', // Duplicate staff across segments
  e00252: 'validation.invalid', // Invalid segment time format
  e00253: 'validation.constraint', // Segment startTime must be before endTime
  e00254: 'validation.constraint', // All segments must be on the same day
  e00255: 'validation.constraint', // Segments overlap
  e00256: 'validation.invalid', // Could not parse segment times
  e00257: 'validation.invalid', // Could not parse segment start/end times for combo expansion
  e00258: 'validation.constraint', // Segment template references missing service
  e00259: 'validation.constraint', // Too many segments (exceeds maximum allowed)

  // Combo service expansion errors (e00260-e00262 moved to e00290 series to avoid conflict)
  e00290: 'resource.notFound', // Segment template references service not found
  e00291: 'validation.constraint', // Could not assign staff for segment
  e00292: 'validation.constraint', // Could not assign unique staff for segment

  // Multi-slots API errors (e00280 series)
  e00280: 'validation.required', // Multi-slots: date is required
  e00281: 'validation.required', // Multi-slots: segments parameter is required
  e00282: 'validation.invalid', // Multi-slots: segments must be valid JSON array
  e00283: 'validation.constraint', // Multi-slots: segments must have at least 2 items
  e00284: 'validation.invalid', // Multi-slots: segment must have serviceId
  e00285: 'validation.invalid', // Multi-slots: validation error
  e00286: 'system.internal', // Multi-slots: internal error
  e00287: 'system.internal', // Multi-slots: internal error (general)
  e00288: 'validation.constraint', // Unified slots: provide either service OR segments, not both

  // Refresh token errors (e00270 series)
  e00270: 'validation.required', // Refresh token is required
  e00271: 'auth.unauthorized', // Invalid or expired refresh token

  // Google sign-in errors (e00293 series)
  e00293: 'auth.tokenInvalid', // Google ID token failed verification
  e00294: 'auth.emailNotVerified', // Google account email is not verified
  e00295: 'auth.unauthorized', // Google sign-in could not establish a session
  e00296: 'resource.notFound', // No customer account found for this Google email
  e00297: 'auth.unauthorized', // No staff account for this Google email — invitation required
  e00298: 'resource.conflict', // Google sign-in: orphaned ba_user (account exists) — sign in instead

  // Payment-specific errors (e00300 series)
  e00300: 'validation.invalid', // Sumup not connected / Failed to initiate payment
  e00301: 'resource.notFound', // Payment not found
  e00302: 'validation.invalid', // Refund failed
  e00303: 'resource.notFound', // Booking not found (payment context)
  e00304: 'resource.conflict', // Payment already pending
  e00305: 'validation.invalid', // Invalid payment status
  e00306: 'validation.invalid', // Currency mismatch
  e00307: 'system.external', // Sumup API error
  e00308: 'device.unavailable', // Reader offline - device not powered on or connected (KAS-038: 422, not 502)
  e00309: 'system.external', // Reader busy - another transaction in progress
  e00320: 'system.timeout', // Reader timeout - device did not respond
  e00321: 'system.external', // Reader connection failed - network issue
  e00330: 'resource.notFound', // Payment link not found / invalid token
  e00331: 'validation.invalid', // Merchant payment connection unavailable for online checkout
  e00332: 'system.internal', // Failed to load payment link / lazy-create checkout
  e00333: 'resource.conflict', // Booking already has an active payment link
  e00334: 'validation.invalid', // Booking customer has no deliverable email for pay-by-link

  // Validation constraint errors (e00310 series)
  e00310: 'validation.constraint', // perPage value too large (exceeds maximum)
  e00311: 'validation.constraint', // page value too small (below minimum)
  e00312: 'validation.constraint', // Value too small (generic minimum constraint)
  e00313: 'validation.constraint', // Value too large (generic maximum constraint)
  e00314: 'validation.format', // Invalid date format
  e00315: 'validation.format', // Invalid email format
  e00316: 'validation.format', // Invalid URL format
  e00317: 'validation.format', // Invalid enum value
  e00318: 'validation.required', // Missing required field
  e00319: 'validation.format', // Invalid string format

  // Voucher/Promotion errors (e00400 series)
  e00400: 'validation.invalid', // Failed to create promotion
  e00401: 'system.internal', // Promotion creation error
  e00402: 'resource.notFound', // Promotion not found
  e00404: 'validation.invalid', // Failed to update promotion
  e00405: 'validation.invalid', // Failed to generate vouchers
  e00406: 'resource.notFound', // Voucher not found
  e00407: 'validation.invalid', // Voucher already distributed
  e00408: 'validation.invalid', // Voucher already redeemed
  e00409: 'validation.invalid', // Voucher expired
  e00410: 'validation.invalid', // Voucher not valid for this shop
  e00411: 'validation.constraint', // Minimum purchase amount not met
  e00412: 'validation.constraint', // Maximum redemptions reached
  e00413: 'validation.constraint', // Promotion inactive
  e00414: 'validation.invalid', // Invalid discount type or value
  e00415: 'system.internal', // Voucher operation error
  e00416: 'validation.invalid', // Failed to delete voucher
  e00417: 'validation.invalid', // Failed to list vouchers
  e00418: 'validation.invalid', // Failed to validate voucher
  e00419: 'validation.required', // Voucher code is required
  e00420: 'validation.invalid', // Failed to list promotions
  e00421: 'system.internal', // Bulk distribute vouchers internal error
  e00422: 'system.internal', // Validate voucher internal error
  e00423: 'validation.invalid', // Apply voucher validation error
  e00424: 'system.internal', // Apply voucher internal error
  e00425: 'resource.notFound', // Voucher not found (get single)
  e00426: 'system.internal', // Get voucher internal error
  e00427: 'validation.invalid', // Cancel voucher validation error
  e00428: 'system.internal', // Cancel voucher internal error
  e00429: 'resource.conflict', // Flow add-on decrease blocked — active Kasse exceed requested Flow quota
  e00430: 'validation.required', // Shop ID is required

  // Voucher email distribution errors (e00430 series)
  e00431: 'validation.invalid', // Failed to send voucher email (validation)
  e00432: 'system.internal', // Failed to send voucher email (internal error)
  e00433: 'validation.invalid', // Bulk email distribution failed
  e00434: 'system.internal', // Bulk email distribution internal error

  // Staff availability shop-level errors (e00350 series)
  e00350: 'resource.notFound', // Shop not found (staff availability context)
  e00352: 'system.internal', // Failed to get staff availabilities for shop

  // Generic QA / scheduler error — used by backend/src/routes/v2/qa/qa.ts
  // as the catch-all for internal failures (seed/org, scenarios CRUD,
  // runs-introspect, runner-update). Pre-existing callsites threw this
  // for years without it being mapped; surfaced when an actual seed/org
  // 500 hit the catch and the registry meta-threw "Unknown error code".
  e00500: 'system.internal',

  // Product errors (e00500 series)
  e00501: 'resource.notFound', // Product not found
  e00502: 'resource.duplicate', // Duplicate SKU
  e00503: 'resource.duplicate', // Duplicate barcode
  e00504: 'validation.invalid', // Product create validation
  e00505: 'system.internal', // Product create internal error
  e00506: 'validation.invalid', // Product update validation
  e00507: 'system.internal', // Product update internal error
  e00508: 'system.internal', // Product delete internal error
  e00509: 'system.internal', // Product list internal error
  e00510: 'system.internal', // Product get internal error

  // Product inventory errors (e00520 series)
  e00520: 'validation.constraint', // Insufficient inventory
  e00521: 'validation.constraint', // Invalid quantity (cannot reduce below zero)
  e00522: 'validation.invalid', // Inventory adjust validation
  e00523: 'system.internal', // Inventory adjust internal error
  e00524: 'system.internal', // Inventory deduct internal error

  // Product search errors (e00540 series)
  e00540: 'validation.invalid', // Search validation
  e00541: 'system.internal', // Search internal error

  // Product permission errors (e00550 series)
  e00550: 'resource.notFound', // Shop not found (product context)
  e00551: 'permission.denied', // Permission denied (product context)

  // Entity notes errors (e00600 series)
  e00600: 'resource.notFound', // Note not found
  e00601: 'validation.invalid', // Create validation failed
  e00602: 'system.internal', // Create internal error
  e00603: 'validation.invalid', // Update validation failed
  e00604: 'system.internal', // Update internal error
  e00605: 'system.internal', // Delete internal error
  e00606: 'system.internal', // List internal error
  e00607: 'permission.denied', // Not the author (and not admin)

  // Operations module errors (e00610 series)
  e00610: 'system.internal', // Operations: load state failed
  e00611: 'system.internal', // Operations: list admins failed
  e00620: 'system.internal', // Operations: attachment storage not configured
  e00621: 'validation.invalid', // Operations: attachment upload missing file
  e00622: 'system.internal', // Operations: attachment upload failed

  // Account deletion errors (e00700 series)
  e00700: 'resource.conflict', // Account deletion in progress (blocks registration/login)
  e00702: 'system.internal', // Deletion status check failed
  e00703: 'system.internal', // Deletion retry failed
  e00704: 'system.internal', // Deletion clear failed
  e00705: 'validation.invalid', // Deletion retry validation error
  e00706: 'validation.invalid', // Deletion clear validation error
  e00707: 'resource.notFound', // User not found (admin deletion)
  e00708: 'resource.conflict', // Deletion already in progress (admin deletion)
  e00709: 'system.internal', // Failed to initiate deletion (admin deletion)
  e00710: 'validation.invalid', // Cleanup: could not determine email
  e00711: 'system.internal', // Cleanup: failed to clean up orphaned records

  // Business crawl errors (e00712 series)
  e00712: 'validation.invalid', // No website URL determinable
  e00713: 'system.internal', // Browser Rendering API error
  e00714: 'system.internal', // Crawl timeout or empty result
  e00715: 'system.internal', // Get crawl result failed

  // Guest checkout errors
  e00716: 'validation.invalid', // Guest checkout: plan is required
  e00717: 'validation.invalid', // Guest checkout: invalid subscription plan
  e00718: 'system.internal', // Guest checkout: failed to create session
  e00719: 'system.internal', // Guest checkout: missing FRONTEND_URL

  // Consent email errors (e00730 series)
  e00730: 'validation.invalid', // Consent token invalid or expired
  e00731: 'system.internal', // Consent email send failed
  e00732: 'validation.invalid', // User has dummy email, cannot send consent
  e00733: 'resource.notFound', // User not found for consent token

  // Membership errors (e00800 series)
  e00800: 'resource.notFound', // Plan not found
  e00801: 'validation.invalid', // Plan create validation
  e00802: 'system.internal', // Plan create internal
  e00803: 'validation.invalid', // Plan update validation
  e00804: 'system.internal', // Plan update internal
  e00805: 'system.internal', // Plan delete internal
  e00806: 'system.internal', // Plan list internal
  e00807: 'system.internal', // Plan get internal
  e00820: 'resource.notFound', // Membership not found
  e00821: 'system.internal', // Membership create internal
  e00822: 'resource.conflict', // Membership already active
  e00823: 'system.internal', // Membership cancel internal
  e00824: 'system.internal', // Membership renew internal
  e00825: 'system.internal', // Membership list internal
  e00826: 'system.internal', // Membership get internal
  e00850: 'permission.denied', // Membership permission denied

  // Intake errors (e00900 series) — ADR 0001, ADR 0002, issue 0003
  e00900: 'permission.denied', // Booking blocked by intake policy (verdict != BOOK_FREELY)
  e00901: 'validation.constraint', // Illegal Intake state transition
  e00902: 'resource.conflict', // Intake already exists (UNIQUE(customer, staff) violation)
  e00903: 'validation.invalid', // Intake invalid argument

  // KAS-037 — shop update blocked: active cash registers (400/validation)
  e00435: 'validation.invalid', // Shop has active cash registers; decommission before leaving fiscal country

  // KAS-066 — refund blocked: payment awaiting TSE re-signing after an outage (400/validation)
  e00436: 'validation.invalid', // Refund not possible while payment is outage_unsigned; retry once fiscally signed

  // KAS — fiskaly SUBMIT DE submission lifecycle errors (e00440–e00454).
  // These are surfaced by backend/src/routes/v2/fiscal-submissions/fiscal-submissions.ts.
  // Previously UNMAPPED — a fiskaly build/validation failure (e.g. e00442) hit
  // internalErrorResponse(c, 'e00442', …), whose createStandardError →
  // resolveErrorCode THREW "Unknown error code" while building the error
  // response → the handler 500'd instead of returning a clean coded error. Each
  // code is mapped to the standard ErrorCode matching the HTTP status the route
  // returns (system.internal=500, resource.notFound=404, resource.conflict=409,
  // validation.invalid=422/400, system.external=502).
  e00440: 'resource.notFound', // Shop not found
  e00441: 'resource.notFound', // Submission not found
  e00442: 'system.internal', // Build failed (uncaught — the live-run 500)
  e00443: 'validation.invalid', // Validation failed (ERiC / hard error)
  e00444: 'resource.conflict', // Preview not ready / unrecoverable past fetch point
  e00445: 'system.internal', // Confirm failed
  e00446: 'resource.conflict', // Not confirmed (transmit precondition)
  e00447: 'resource.conflict', // No sealed preview (confirm/transmit precondition)
  e00448: 'system.external', // Transmit failed at provider (502)
  e00449: 'validation.invalid', // Transmission rejected by ELSTER (422)
  e00450: 'resource.conflict', // Proof not ready
  e00451: 'system.internal', // SUBMIT DE provider / archive binding not configured
  e00452: 'validation.invalid', // Invalid submission id
  e00453: 'system.internal', // Status read failed
  e00454: 'resource.conflict', // Already transmitted (rebuild refused)

  // KAS-068 — org-level fiscal taxpayer master-data errors (e00460–e00465).
  // Surfaced by backend/src/routes/v2/organisations/organisations.ts. (Replaces
  // the removed standalone Establishment routes — a shop IS the establishment.)
  e00460: 'resource.notFound', // Organisation not found
  e00461: 'system.internal', // Read org fiscal failed
  e00462: 'system.internal', // Update org fiscal failed
  e00463: 'validation.invalid', // Invalid Steuernummer
  e00464: 'system.internal', // Fiscal consent record failed
  e00465: 'validation.invalid', // Invalid taxpayer_type
  // KAS — SUBMIT DE taxpayer detail fields. e00466 is the build-time guard
  // (fiscal-submissions.ts): the org has a Steuernummer but the required detail
  // fields for its taxpayer type are missing → 422 BEFORE the fiskaly taxpayer
  // PUT (which would 400 → 500). e00467/e00468 are org-fiscal field validation.
  e00466: 'validation.invalid', // Taxpayer details incomplete (complete before filing)
  e00467: 'validation.invalid', // Invalid legal_form code
  e00468: 'validation.invalid', // Invalid identification_number (IdNr)

  // Admin deals errors (e01400 series)
  e01400: 'system.internal', // Deals sync failed
  e01401: 'system.internal', // Get sync status failed
  e01402: 'system.internal', // Get deals list failed
  e01403: 'resource.notFound', // Deal not found in cache
  e01404: 'system.internal', // Get single deal failed
  e01410: 'resource.notFound', // Deal not found (crawl)
  e01411: 'system.internal', // Crawl failed
  e01412: 'resource.notFound', // Deal not found (summary generation)
  e01413: 'system.internal', // Summary generation failed
  e01414: 'system.internal', // Get summary failed
  e01415: 'validation.invalid', // No crawl URL determinable
  e01416: 'resource.notFound', // Sales summary HTML not found (public)
  e01417: 'system.internal', // Failed to serve sales summary (public)

  // Demo account errors (e01420 series)
  e01420: 'resource.notFound', // Deal not found (generate demo)
  e01421: 'validation.invalid', // Demo already exists
  e01422: 'system.internal', // Demo generation failed
  e01423: 'resource.notFound', // No demo exists (delete)
  e01424: 'system.internal', // Demo deletion failed
  e01425: 'resource.notFound', // Deal not found (setup request)
  e01426: 'validation.invalid', // No contact email (setup request)
  e01427: 'system.internal', // Failed to create support ticket
  e01428: 'system.internal', // Failed to init setup request
  e01429: 'system.internal', // Setup request creation failed
  e01430: 'system.internal', // OTP regeneration failed

  // Per-pipeline sync errors (e01405-e01407 — DEAL-002)
  e01405: 'validation.invalid', // No HubSpot access token configured (sync)
  e01406: 'validation.invalid', // No pipeline configured (sync)
  e01407: 'system.internal', // Failed to list configured pipelines

  // HubSpot settings errors (e01440 series)
  e01440: 'system.internal', // Failed to read deals settings
  e01441: 'system.internal', // Failed to update deals settings
  e01442: 'system.internal', // Failed to list HubSpot pipelines
  e01443: 'system.internal', // Failed to list HubSpot owners
  e01444: 'system.internal', // Failed to test HubSpot connection

  // Stage move errors (e01450 series — DEAL-001)
  e01450: 'validation.required', // dealStage is required
  e01451: 'resource.notFound', // Deal not found (stage move)
  e01452: 'validation.invalid', // Invalid stage move (cross-pipeline / unknown stage)
  e01453: 'system.internal', // Stage update failed

  // Configured-pipeline list editor errors (e01460 series — DEAL-003/004/005/006)
  e01460: 'validation.required', // pipelineId is required (add)
  e01461: 'validation.invalid', // HubSpot access token not configured (add)
  e01462: 'validation.invalid', // Pipeline already configured (duplicate add)
  e01463: 'system.internal', // Failed to persist settings (add)
  e01464: 'system.internal', // Failed to add pipeline
  e01465: 'resource.notFound', // Pipeline not configured (patch)
  e01466: 'system.internal', // Failed to persist settings (patch)
  e01467: 'system.internal', // Failed to update pipeline
  e01468: 'validation.invalid', // Country already claimed (add)
  e01469: 'validation.invalid', // Country already claimed (patch)
  e01470: 'resource.notFound', // Pipeline not configured (delete)
  e01471: 'system.internal', // Failed to persist settings (delete)
  e01472: 'system.internal', // Failed to remove pipeline

  // Translation admin errors (e07000 series)
  e07001: 'system.internal', // Failed to list languages
  e07002: 'system.internal', // Failed to list files
  e07003: 'resource.notFound', // Translation file not found
  e07004: 'system.internal', // Failed to read translation file
  e07005: 'system.internal', // Failed to write translation file
  e07006: 'system.internal', // Failed to create translation file
  e07007: 'system.internal', // Failed to delete translation file
  e07008: 'validation.invalid', // Filename must end with .json
  e07010: 'resource.notFound', // Public translation file not found
  e07011: 'system.internal', // Failed to read public translation file
  e07020: 'validation.invalid', // Files array is required and must not be empty
  e07021: 'validation.invalid', // All filenames must end with .json
  e07022: 'system.internal', // Failed to upload files in batch
  e07030: 'validation.required', // Key is required (split)
  e07031: 'resource.notFound', // Source file not found (split)
  e07032: 'validation.invalid', // Key is not an object (split)
  e07033: 'system.internal', // Failed to split object to file
};

// Helper function to resolve unique error codes to ErrorCode types
export function resolveErrorCode(code: string | ErrorCode): ErrorCode {
  // Handle edge case where Zod error array is accidentally passed as string
  if (
    typeof code === 'string' &&
    code.startsWith('[') &&
    code.includes('"code":')
  ) {
    console.error(
      'Detected Zod error array passed as error code:',
      code.substring(0, 200) + '...',
    );

    try {
      // Try to parse the Zod error array and extract the first error
      const zodErrors = JSON.parse(code);
      if (
        Array.isArray(zodErrors) &&
        zodErrors.length > 0 &&
        zodErrors[0].code
      ) {
        const firstError = zodErrors[0];

        // Use shared mapping function to get error code, then resolve to ErrorCode
        const errorCode = mapZodIssueToErrorCode(firstError);
        if (errorCode && UNIQUE_ERROR_CODE_MAPPING[errorCode]) {
          return UNIQUE_ERROR_CODE_MAPPING[errorCode];
        }
      }
    } catch (parseError) {
      console.error('Failed to parse Zod error array:', parseError);
    }

    // Fallback to generic validation error
    return 'validation.invalid';
  }

  // If it's already an ErrorCode type, return as-is
  if (code in ERROR_CONFIGS) {
    return code as ErrorCode;
  }

  // If it's a unique error code, resolve it
  const resolvedCode = UNIQUE_ERROR_CODE_MAPPING[code];
  if (resolvedCode) {
    return resolvedCode;
  }

  // No fallback - throw error for unknown codes
  throw new Error(
    `Unknown error code: ${code}. All error codes must be properly mapped.`,
  );
}
